/** 입고수량 자동입력 기본 계산식 (설정 placeholder·자동 채움) */
export const DEFAULT_INCOMING_QTY_FORMULA = '전체 수량 - 수량 부족 = 입고수량';

/** 무상 수리 수량 자동입력 기본 계산식 */
export const DEFAULT_FREE_REPAIR_QTY_FORMULA = '입고 수량 - 반품 수량 - 유상 수리 수량 = 무상 수리 수량';

const MAX_FORMULA_VISIT_DEPTH = 12;

const parseQtyOperand = (raw) => {
    if (raw === '' || raw === null || raw === undefined) {
        return 0;
    }
    const n = Number.parseInt(String(raw).replace(/[^\d-]/g, ''), 10);
    return Number.isNaN(n) ? 0 : Math.min(999999, Math.max(0, n));
};

export const normalizeAsRepairToken = (s) => String(s || '').replace(/\s+/g, '').toLowerCase();

const escapeRegex = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const labelToFlexiblePattern = (label) => (
    String(label || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(escapeRegex)
        .join('\\s*')
);

const safeRegexReplace = (text, pattern, replacement) => {
    if (!pattern || !String(pattern).trim()) {
        return text;
    }
    try {
        return String(text).replace(new RegExp(pattern, 'gi'), replacement);
    } catch (error) {
        return text;
    }
};

/**
 * "전체 수량 - 수량 부족 = 입고수량" → "전체 수량 - 수량 부족"
 */
export const extractAsRepairFormulaExpression = (formula) => {
    const raw = String(formula || '').trim();
    if (!raw) {
        return '';
    }
    if (!raw.includes('=')) {
        return raw;
    }
    const parts = raw.split('=').map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) {
        return raw;
    }
    const left = parts[0];
    const right = parts.slice(1).join('=');
    const hasOp = (s) => /[+\-*/]/.test(s);
    if (hasOp(left) && !hasOp(right)) {
        return left;
    }
    if (hasOp(right) && !hasOp(left)) {
        return right;
    }
    return left;
};

export const getAsRepairNumericOperand = (values, key, fields) => {
    const field = (fields || []).find((f) => f.key === key);
    const raw = values?.[key];
    if (field?.inputType === 'quantity') {
        return parseQtyOperand(raw);
    }
    const n = Number.parseFloat(String(raw ?? '').replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
};

/**
 * @returns {string|number} 계산 결과 (식 미설정·오류 시 빈 문자열)
 */
export function computeAsRepairAutoValue(field, values, fields, visiting = null) {
    if (!field || field.inputType !== 'auto') {
        return '';
    }
    const stack = visiting instanceof Set ? visiting : new Set();
    if (stack.has(field.key) || stack.size >= MAX_FORMULA_VISIT_DEPTH) {
        return '';
    }
    const formula = String(field.formula || '').trim();
    if (!formula) {
        return '';
    }

    stack.add(field.key);
    try {
        const result = evaluateAsRepairFormula(formula, values, fields, field, stack);
        if (result === null || result === undefined) {
            return '';
        }
        return Number.isInteger(result) ? result : Math.round(result * 1000) / 1000;
    } catch (error) {
        return '';
    } finally {
        stack.delete(field.key);
    }
}

/** 자동 입력 필드는 계산된 값으로, 그 외는 입력값으로 피연산자 조회 */
const getFieldNumericValueForFormula = (field, values, fields, selfField, visiting) => {
    if (!field) {
        return 0;
    }
    if (field.inputType === 'auto') {
        if (selfField && field.key === selfField.key) {
            return 0;
        }
        const computed = computeAsRepairAutoValue(field, values, fields, visiting);
        return parseQtyOperand(computed === '' ? 0 : computed);
    }
    return getAsRepairNumericOperand(values, field.key, fields);
};

/** 일괄 치환용 — 자동 입력 필드는 토큰 매칭으로만 참조 (순환 방지) */
const buildSubstitutableFields = (fields, excludeKey = null) => (
    (fields || [])
        .filter((f) => f.key !== excludeKey && f.inputType !== 'auto')
        .map((f) => ({
            key: f.key,
            label: f.label,
            inputType: f.inputType,
            pattern: labelToFlexiblePattern(f.label),
            normLabel: normalizeAsRepairToken(f.label),
            normKey: normalizeAsRepairToken(f.key),
        }))
        .filter((f) => f.normLabel.length >= 2)
        .sort((a, b) => b.normLabel.length - a.normLabel.length)
);

const findFieldByFormulaToken = (token, fields, excludeKey) => {
    const norm = normalizeAsRepairToken(token);
    if (!norm || norm.length < 1) {
        return null;
    }
    const pool = (fields || []).filter((f) => f.key !== excludeKey);
    const exact = pool.find((f) => normalizeAsRepairToken(f.label) === norm);
    if (exact) {
        return exact;
    }
    const keyMatch = pool.find((f) => normalizeAsRepairToken(f.key) === norm);
    if (keyMatch) {
        return keyMatch;
    }
    const fuzzy = pool.filter((f) => {
        const nl = normalizeAsRepairToken(f.label);
        return nl === norm || nl.includes(norm) || norm.includes(nl);
    });
    if (fuzzy.length === 0) {
        return null;
    }
    fuzzy.sort((a, b) => normalizeAsRepairToken(b.label).length - normalizeAsRepairToken(a.label).length);
    return fuzzy[0];
};

/** 식에 한글·영문 토큰이 남을 때 토큰 단위 치환 (자동 입력 필드 포함) */
const substituteByFormulaTokens = (expression, values, fields, selfKey, visiting) => {
    const parts = String(expression || '').split(/([+\-*/])/);
    const rebuilt = parts.map((part) => {
        const trimmed = part.trim();
        if (!trimmed || /^[+\-*/]$/.test(trimmed)) {
            return part;
        }
        const field = findFieldByFormulaToken(trimmed, fields, selfKey);
        if (!field) {
            return part;
        }
        const num = getFieldNumericValueForFormula(field, values, fields, { key: selfKey }, visiting);
        return `(${num})`;
    });
    return rebuilt.join('').replace(/\s+/g, '');
};

const substituteByNormalizedLabels = (expression, values, fields, selfKey, visiting) => {
    let normExpr = normalizeAsRepairToken(expression);
    const list = buildSubstitutableFields(fields, selfKey);
    list.forEach((item) => {
        if (!item.normLabel || item.normLabel.length < 2) {
            return;
        }
        const field = (fields || []).find((f) => f.key === item.key);
        const num = getFieldNumericValueForFormula(field, values, fields, { key: selfKey }, visiting);
        if (normExpr.includes(item.normLabel)) {
            normExpr = normExpr.split(item.normLabel).join(`(${num})`);
        }
    });
    return normExpr;
};

const substituteFieldReferences = (expression, values, fields, selfKey = null, visiting = null) => {
    let expr = String(expression || '');
    const list = buildSubstitutableFields(fields, selfKey);

    list.forEach((item) => {
        const field = (fields || []).find((f) => f.key === item.key);
        const num = getFieldNumericValueForFormula(
            field,
            values,
            fields,
            selfKey ? { key: selfKey } : null,
            visiting,
        );
        if (item.pattern) {
            expr = safeRegexReplace(expr, item.pattern, `(${num})`);
        }
        if (item.key) {
            expr = safeRegexReplace(expr, `\\{${escapeRegex(item.key)}\\}`, `(${num})`);
            expr = safeRegexReplace(expr, `\\b${escapeRegex(item.key)}\\b`, `(${num})`);
        }
    });

    let compact = expr.replace(/\s+/g, '');
    if (/[a-zA-Z가-힣]/.test(compact)) {
        compact = substituteByNormalizedLabels(expression, values, fields, selfKey, visiting);
    }
    if (/[a-zA-Z가-힣]/.test(compact)) {
        compact = substituteByFormulaTokens(expression, values, fields, selfKey, visiting);
    }
    return compact;
};

const safeEvalArithmetic = (expr) => {
    const cleaned = String(expr || '').replace(/\s+/g, '');
    if (!cleaned || !/^[\d+\-*/().]+$/.test(cleaned)) {
        return null;
    }
    try {
        // eslint-disable-next-line no-new-func
        const result = Function(`"use strict"; return (${cleaned});`)();
        return typeof result === 'number' && Number.isFinite(result) ? result : null;
    } catch (error) {
        return null;
    }
};

/** 전체 수량 − 수량 부족 = 입고수량 (라벨·식 표기가 조금 달라도 동작) */
export const resolveIncomingQtyOperandFields = (fields, excludeKey = null) => {
    const qtyFields = (fields || []).filter(
        (f) => f.inputType === 'quantity' && f.key !== excludeKey,
    );
    if (qtyFields.length === 0) {
        return { total: null, shortage: null };
    }
    const total = qtyFields.find((f) => normalizeAsRepairToken(f.label).includes('전체'))
        || qtyFields.find((f) => /total/i.test(f.key))
        || qtyFields[0];
    const shortage = qtyFields.find((f) => {
        if (f.key === total?.key) {
            return false;
        }
        const nl = normalizeAsRepairToken(f.label);
        return nl.includes('부족') || nl.includes('short');
    }) || qtyFields.find((f) => f.key !== total?.key) || null;
    return { total, shortage };
};

const tryBuiltinFreeRepairQtyFormula = (formula, values, fields, selfField, visiting) => {
    const norm = normalizeAsRepairToken(formula);
    if (!norm.includes('무상') || !norm.includes('입고')) {
        return null;
    }
    const pool = (fields || []).filter((f) => f.key !== selfField?.key);
    const incoming = pool.find((f) => {
        const nl = normalizeAsRepairToken(f.label);
        return nl.includes('입고') && (nl.includes('수량') || nl.endsWith('수'));
    });
    const returned = pool.find((f) => normalizeAsRepairToken(f.label).includes('반품'));
    const paid = pool.find((f) => {
        const nl = normalizeAsRepairToken(f.label);
        return nl.includes('유상') && (nl.includes('수리') || nl.includes('수량'));
    });
    if (!incoming) {
        return null;
    }
    const a = getFieldNumericValueForFormula(incoming, values, fields, selfField, visiting);
    const b = returned ? getFieldNumericValueForFormula(returned, values, fields, selfField, visiting) : 0;
    const c = paid ? getFieldNumericValueForFormula(paid, values, fields, selfField, visiting) : 0;
    return Math.min(999999, Math.max(0, a - b - c));
};

const tryBuiltinIncomingQtyFormula = (formula, values, fields, selfField) => {
    const norm = normalizeAsRepairToken(formula);
    if (!norm.includes('전체') || !norm.includes('부족')) {
        return null;
    }
    const { total, shortage } = resolveIncomingQtyOperandFields(fields, selfField?.key || null);
    if (!total || !shortage) {
        return null;
    }
    const a = getAsRepairNumericOperand(values, total.key, fields);
    const b = getAsRepairNumericOperand(values, shortage.key, fields);
    return Math.min(999999, Math.max(0, a - b));
};

const evaluateAsRepairFormula = (formula, values, fields, selfField = null, visiting = null) => {
    const expression = extractAsRepairFormulaExpression(formula);
    if (!expression) {
        return null;
    }
    try {
        const substituted = substituteFieldReferences(
            expression,
            values,
            fields,
            selfField?.key || null,
            visiting,
        );
        let result = safeEvalArithmetic(substituted);
        if (result === null) {
            result = tryBuiltinFreeRepairQtyFormula(formula, values, fields, selfField, visiting);
        }
        if (result === null) {
            result = tryBuiltinIncomingQtyFormula(formula, values, fields, selfField);
        }
        return result;
    } catch (error) {
        return null;
    }
};

export const formatAsRepairAutoDisplay = (computed) => {
    if (computed === '' || computed === null || computed === undefined) {
        return '-';
    }
    return String(computed);
};

export const applyAsRepairAutoValuesToForm = (fields, values) => {
    const list = Array.isArray(fields) ? fields : [];
    let next = { ...values };
    const autoFields = list.filter((f) => f.inputType === 'auto');
    if (autoFields.length === 0) {
        return next;
    }
    try {
        for (let pass = 0; pass < 5; pass += 1) {
            let changed = false;
            autoFields.forEach((field) => {
                const computed = computeAsRepairAutoValue(field, next, list);
                if (next[field.key] !== computed) {
                    next[field.key] = computed;
                    changed = true;
                }
            });
            if (!changed) {
                break;
            }
        }
    } catch (error) {
        return values;
    }
    return next;
};

export const suggestAsRepairAutoFormula = (label) => {
    const norm = normalizeAsRepairToken(label);
    if (norm.includes('무상') && (norm.includes('수리') || norm.includes('수량'))) {
        return DEFAULT_FREE_REPAIR_QTY_FORMULA;
    }
    if (norm.includes('입고') && (norm.includes('수량') || norm.endsWith('수'))) {
        return DEFAULT_INCOMING_QTY_FORMULA;
    }
    return '';
};
